import {UIBaseShareImage} from './UIBaseShareImage'
import {UIGamePlayerInformationView} from '../ui_game/ui_game_players/UIGamePlayerInformationView'
import {UIGameSpriteHandler} from '../UIGameSpriteHandler'
import {createDivIn, createExternalImgIn, createParagraphIn} from '../ui_utils/DomUtils'
import {MapSettingEnum, VictoryPointStateReader} from '@colonist/game-base-types'
import {UIImagePathController} from '../UIEnums.ts'
import {SectionBgView} from '../ui_canvas_engine/SectionBgView'
import {PlayerEnums} from '../../../../shared/GameEnums'
import {PlayerColor} from '@colonist/game-states'

export class UIEndGameShareImage extends UIBaseShareImage {
    private winningColor: PlayerColor
    protected contentContainer: HTMLDivElement

    protected async generateImage() {
        this.contentContainer = createDivIn(this.container, 'share-image-content-container')
        await this.createMap()
        this.createPlayers()
    }

    private async createMap() {
        const mapView = this.uiGameManager.mapController.mapView

        const viewportBackgroundIndex = 0

        const isElementMapBackground = mapView.getChildAt(viewportBackgroundIndex) instanceof SectionBgView
        if(isElementMapBackground) mapView.removeChildAt(viewportBackgroundIndex)

        const mapImage = await this.extract.image(mapView, 'image/png')
        mapImage.classList.add('share-image-map')

        if(this.isWideMap()) mapImage.classList.add('wide-map')
        if(this.isTallMap()) mapImage.classList.add('tall-map')

        this.contentContainer.appendChild(mapImage)
    }

    private createPlayers() {
        this.setWinningPlayerColor()

        const playerViews = this.uiGameManager.playersController.playerInformationViews
        this.sortPlayerViews(playerViews)

        const playersDiv = createDivIn(this.contentContainer, 'share-image-players')
        const topRow = createDivIn(playersDiv, 'share-image-players-row')
        const bottomRow = createDivIn(playersDiv, 'share-image-players-row')

        for(let i = 0; i < playerViews.length; i++) {
            const shouldBeOnTopRow = UIEndGameShareImage.isTopRowPlayer(i, playerViews.length)
            const row = shouldBeOnTopRow ? topRow : bottomRow
            const playerDiv = createDivIn(row, 'share-image-player')

            const playerView = playerViews[i]

            this.createPlayerAvatar(playerDiv, playerView)
            this.createPlayerRibbon(playerDiv, playerView)
            this.createPlayerUsername(playerDiv, playerView)

            if(!this.shouldCreatePlayerCrown(playerView, playerViews.length)) continue

            UIEndGameShareImage.createPlayerCrown(playerDiv)
        }

        UIEndGameShareImage.setPlayersStyle(playersDiv, playerViews.length)
    }

    // eslint-disable-next-line class-methods-use-this
    protected createPlayerAvatar(container: HTMLDivElement, playerView: UIGamePlayerInformationView) {
        const iconContainer = createDivIn(container, 'share-image-player-background')
        iconContainer.style.backgroundImage = `url('/dist/images/player_bg_${UIGameSpriteHandler.mapPlayerColorToColorId(playerView.player.color)}.svg?rev=')`

        if(playerView.player.profilePictureUrl != undefined) {
            UIEndGameShareImage.createPlayerProfilePicture(iconContainer, playerView.player.profilePictureUrl)
            return
        }

        UIEndGameShareImage.createPlayerIcon(iconContainer, playerView)
    }

    private static createPlayerProfilePicture(container: HTMLDivElement, profilePictureUrl: string) {
        const profilePicture = createDivIn(container, `share-image-player-profile-picture`)
        profilePicture.style.backgroundImage = `url('${profilePictureUrl}')`
    }

    private static createPlayerIcon(container: HTMLDivElement, playerView: UIGamePlayerInformationView) {
        const iconClass = 'share-image-player-icon'
        const iconSrc = `/dist/images/${UIImagePathController.getImgPath(playerView.player.displayIcon)}?rev=`

        const playerIcon = createExternalImgIn(container, iconSrc, {key: 'strings:colonistCommon.utils.emptyString'}, iconClass)

        if(!playerView.player.isUser) playerIcon.style.padding = '25%'
    }

    private createPlayerRibbon(container: HTMLDivElement, playerView: UIGamePlayerInformationView) {
        createExternalImgIn(container, '/dist/images/ribbon_short.svg?rev=', {key: 'strings:colonistCommon.utils.emptyString'}, 'share-image-player-ribbon')

        const pointsContainer = createDivIn(container, 'share-image-points-container')

        const player = this.endGameState.players[playerView.player.color]
        const victoryPoints = player ? VictoryPointStateReader.totalPrivateVictoryPoints(player.victoryPoints) : 0
        createParagraphIn(pointsContainer, {key: 'strings:colonistCommon.utils.#buffer', options: {value: victoryPoints}}, 'share-image-victory-points')
    }

    private createPlayerUsername(container: HTMLDivElement, playerView: UIGamePlayerInformationView) {
        const playerColor = playerView.playerState.color
        const username = this.uiGameManager.gameController.getPlayerState(playerColor).username
        const truncatedUsername = UIEndGameShareImage.getTruncatedUsername(username)
        createParagraphIn(container, {key: 'strings:colonistCommon.utils.#buffer', options: {value: truncatedUsername}}, 'share-image-player-username')
    }

    private static createPlayerCrown(container: HTMLDivElement) {
        createExternalImgIn(container, '/dist/images/icon_crown.svg?rev=', {key: 'strings:colonistCommon.utils.emptyString'}, 'share-image-player-crown')
    }

    private isWideMap(): boolean {
        const wideMaps: MapSettingEnum[] = [MapSettingEnum.Earth, MapSettingEnum.EarthSeafarers, MapSettingEnum.USA]
        return wideMaps.includes(this.uiGameManager.gameController.gameSettings.mapSetting)
    }

    private isTallMap(): boolean {
        const tallMaps: MapSettingEnum[] = [MapSettingEnum.UK, MapSettingEnum.UKSeafarers]
        return tallMaps.includes(this.uiGameManager.gameController.gameSettings.mapSetting)
    }

    private setWinningPlayerColor() {
        const players = Object.values(this.endGameState.players)
        const winningPlayer = players.find((player) => player.rank == 1)
        this.winningColor = winningPlayer?.color ?? PlayerColor.None
    }

    private static isTopRowPlayer(playerIndex: number, numPlayers: number): boolean {
        if(numPlayers < 3) return true
        if(playerIndex > ((numPlayers - 1) / 2)) return true
        return false
    }

    protected shouldCreatePlayerCrown(playerView: UIGamePlayerInformationView, numPlayers: number): boolean {
        if(numPlayers == 1) return false
        return playerView.player.color == this.winningColor
    }

    protected sortPlayerViews(playerViews: UIGamePlayerInformationView[]) {
        const winnerIndex = playerViews.findIndex((playerView) => playerView.player.color == this.winningColor)

        if(winnerIndex == -1) return

        const lastPlayer = playerViews[playerViews.length - 1]
        playerViews[playerViews.length - 1] = playerViews[winnerIndex]
        playerViews[winnerIndex] = lastPlayer
    }

    private static getTruncatedUsername(username: string): string {
        if(username.length <= PlayerEnums.PlayerConstants.MAX_USERNAME_LENGTH) return username

        const charSpaceForDots = 2
        const truncatedUsername = username.substring(0, PlayerEnums.PlayerConstants.MAX_USERNAME_LENGTH - charSpaceForDots)
        return truncatedUsername + '...'
    }

    private static setPlayersStyle(element: HTMLElement, numPlayers: number) {
        switch(numPlayers) {
            case 3: element.classList.add('three-players'); return
            case 4: element.classList.add('four-players'); return
            case 5: element.classList.add('five-players'); return
            case 6: element.classList.add('six-players'); return
            case 7: element.classList.add('seven-players'); return
            case 8: element.classList.add('eight-players'); return
            default: return
        }
    }
}
